import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PublicLayout } from '@/components/layout'
import { Skeleton } from '@/components/ui/skeleton'
import { getGames } from './api'
import type { Game } from './types'

// 游戏库列表页
export function Games() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [category, setCategory] = useState<string>('')

  const { data, isLoading } = useQuery({
    queryKey: ['games', category],
    queryFn: () => getGames(category),
  })

  const games = data?.data?.items || []
  const categories = [...new Set(games.map((g) => g.category).filter(Boolean))]

  const handlePlay = (game: Game) => {
    navigate({ to: `/games/$gameId`, params: { gameId: String(game.id) } })
  }

  return (
    <PublicLayout>
      <div className='container mx-auto max-w-6xl px-4 py-8'>
        {/* 标题区 */}
        <div className='mb-8 text-center'>
          <h1 className='text-3xl font-bold tracking-tight sm:text-4xl'>
            {t('游戏库')}
          </h1>
          <p className='mt-2 text-muted-foreground'>
            {t('玩转文字游戏，赢取 CHY-API 额度奖励')}
          </p>
        </div>

        {/* 分类筛选 */}
        {categories.length > 0 && (
          <div className='mb-6 flex flex-wrap items-center justify-center gap-2'>
            <button
              onClick={() => setCategory('')}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                !category
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              }`}
            >
              {t('全部')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat!)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${
                  category === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* 游戏网格 */}
        {isLoading ? (
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className='h-48 rounded-2xl' />
            ))}
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
            {games.map((game) => (
              <GameCard key={game.id} game={game} onPlay={() => handlePlay(game)} />
            ))}
          </div>
        )}

        {!isLoading && games.length === 0 && (
          <div className='flex flex-col items-center justify-center py-20 text-muted-foreground'>
            <span className='text-5xl mb-4'>🎮</span>
            <p>{t('暂无游戏')}</p>
          </div>
        )}
      </div>
    </PublicLayout>
  )
}

// 游戏卡片
function GameCard({ game, onPlay }: { game: Game; onPlay: () => void }) {
  const { t } = useTranslation()
  const isMarkdown = game.play_type === 'markdown'
  const hasReward = game.reward_quota > 0

  return (
    <div
      onClick={onPlay}
      className='group relative cursor-pointer overflow-hidden rounded-2xl border bg-card p-6 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
    >
      {/* 图标 */}
      <div className='mb-4 flex items-center justify-between'>
        <span className='text-4xl'>{game.icon || '🎮'}</span>
        {hasReward && (
          <span className='rounded-full bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-600 dark:text-amber-400'>
            🎁 +{game.reward_quota}
          </span>
        )}
      </div>

      {/* 标题 */}
      <h3 className='mb-1 text-lg font-semibold'>
        {game.name}
      </h3>

      {/* 分类 */}
      {game.category && (
        <p className='mb-2 text-xs text-muted-foreground'>{game.category}</p>
      )}

      {/* 描述 */}
      <p className='line-clamp-3 text-sm text-muted-foreground'>
        {game.description}
      </p>

      {/* 标签 */}
      {game.tags && (
        <div className='mt-3 flex flex-wrap gap-1'>
          {game.tags.split(',').slice(0, 3).map((tag) => (
            <span
              key={tag}
              className='rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground'
            >
              {tag.trim()}
            </span>
          ))}
        </div>
      )}

      {/* 播放次数 */}
      <div className='mt-4 flex items-center justify-between text-xs text-muted-foreground'>
        <span>{t('播放')} {game.play_count}</span>
        <span className='font-medium text-primary group-hover:underline'>
          {isMarkdown ? t('查看规则') : t('开始游戏')} →
        </span>
      </div>
    </div>
  )
}
